import L from "leaflet";
import type { LonLatBounds } from "../lib/mercator";
import { CONUS_ATTRIBUTION_HTML } from "../basemap/config";
import { addWorldBasemap, createConusBasemap } from "../basemap/leafletBasemap";

/** Pre-M003 US constants, kept for anything that still imports them directly. */
export const MIN_ZOOM = 3;
export const MAX_ZOOM = 10;

/**
 * Extra proportional padding for `setMaxBounds`, per region (M003): CONUS is
 * huge relative to its own extent so a generous 0.6 pad still feels tight;
 * DWD's much smaller Germany bbox only needs DESIGN.md Aneks M003 §R3's
 * "+~2°"-ish margin, which a much smaller proportional pad approximates.
 */
const MAX_BOUNDS_PAD: Record<string, number> = {
  us: 0.6,
  "eu-de": 0.2
};

export interface RegionMapConfig {
  region: string;
  fitBounds: LonLatBounds;
  minZoom: number;
  maxZoom: number;
}

export interface MapViewHandle {
  map: L.Map;
  attribution: L.Control.Attribution;
  /**
   * D-017: podkład zależy od regionu — „us" = USGS + wektory Natural Earth
   * (stos „jasna"), pozostałe (EUROPA) = NASA GIBS (USGS nie pokrywa Europy).
   * Atrybucja tła przełącza się razem z warstwami (pole `attribution` kafli).
   */
  setBasemapRegion(region: string): void;
}

function toLatLngBounds(bounds: LonLatBounds): L.LatLngBounds {
  return L.latLngBounds([bounds.south, bounds.west], [bounds.north, bounds.east]);
}

/**
 * Apply a region's bounds/zoom to an existing map (init AND region-switch
 * share this — DESIGN.md Aneks M003 §R2.3). `animate: true` flies to the new
 * bounds (region switch); otherwise it snaps immediately (cold boot).
 */
export function applyRegionMapConfig(map: L.Map, cfg: RegionMapConfig, opts?: { animate?: boolean }): void {
  const bounds = toLatLngBounds(cfg.fitBounds);
  const pad = MAX_BOUNDS_PAD[cfg.region] ?? 0.5;
  map.setMinZoom(cfg.minZoom);
  map.setMaxZoom(cfg.maxZoom);
  map.setMaxBounds(bounds.pad(pad));
  if (opts?.animate) {
    map.flyToBounds(bounds, { padding: [8, 8], duration: 1.2 });
  } else {
    map.fitBounds(bounds, { padding: [8, 8] });
  }
}

/** Swap the single "data source" attribution entry without touching the basemap's (DESIGN.md Aneks M003 §R3). */
export function setDataAttribution(control: L.Control.Attribution, previous: string | null, next: string): void {
  if (previous !== null) control.removeAttribution(previous);
  control.addAttribution(next);
}

/**
 * Creates the base Leaflet map: keyless basemap per region (D-017), the given region's
 * extent, zoom limits and a soft max-bounds so the user cannot pan away into
 * an empty world (DECISIONS.md D-002 / DESIGN.md §4 map-controls column).
 */
export function createMapView(container: HTMLElement, initial: RegionMapConfig, initialDataAttribution: string): MapViewHandle {
  const map = L.map(container, {
    minZoom: initial.minZoom,
    maxZoom: initial.maxZoom,
    zoomControl: false,
    attributionControl: false,
    worldCopyJump: false
  });

  L.control.zoom({ position: "topright" }).addTo(map);
  const attribution = L.control
    .attribution({ position: "bottomleft", prefix: false })
    .addAttribution(initialDataAttribution)
    .addTo(map);

  // D-017: kafle bez klucza. Atrybucja tła siedzi na warstwach, więc kontrolka
  // pokazuje dokładnie to tło, które jest na mapie.
  const conus = createConusBasemap(map, { maxZoom: 20, initial: "jasna", attributionHtml: CONUS_ATTRIBUTION_HTML });
  let world: L.TileLayer[] | null = null;
  let applied: "us" | "world" = "us";
  function setBasemapRegion(region: string): void {
    const want = region === "us" ? "us" : "world";
    if (want === applied) return;
    applied = want;
    conus.setVisible(want === "us");
    if (want === "us") {
      for (const l of world ?? []) l.remove();
      world = null;
    } else {
      world = addWorldBasemap(map, { maxZoom: 20, ensureAttribution: false });
    }
  }
  setBasemapRegion(initial.region);

  applyRegionMapConfig(map, initial);
  // The container may not be laid out yet at construction time; a zero-size
  // container makes fitBounds land on max zoom at the bbox centre.
  requestAnimationFrame(() => {
    map.invalidateSize();
    applyRegionMapConfig(map, initial);
  });

  return { map, attribution, setBasemapRegion };
}
